import { PrivacyDownloadError } from "@lafayette/api-client";

import { describeStaffFailure, neutralForbidden } from "../failure-words";

// How the privacy screens tell a staff member what went wrong. Rules:
//   1. No id, name or provider text goes into a message. The API sends none: a refusal holds a code.
//   2. A refusal for access (403) and a missing id (404) read the same: the page never says whether a customer exists.
//   3. A request that changed while the person looked (409) says that nothing was changed, and the page behind reads again.

export type PrivacyFailure = Readonly<{
  kind:
    | "conflict"
    | "bad_request"
    | "forbidden"
    | "unauthenticated"
    | "rate_limited"
    | "unavailable"
    | "network"
    | "unknown";
  message: string;
  /** The same action may be tried again. */
  retryable: boolean;
  /** The page must read again: what the person saw is out of date. */
  reload: boolean;
}>;

const NEUTRAL_FORBIDDEN = neutralForbidden("this", "an administrator");

const make = (
  kind: PrivacyFailure["kind"],
  message: string,
  extra: Partial<Pick<PrivacyFailure, "retryable" | "reload">> = {},
): PrivacyFailure => ({ kind, message, retryable: false, reload: false, ...extra });

export function describePrivacyError(error: unknown): PrivacyFailure {
  // The export download is the one call outside the shared failure shape: a 404 there is "the file is gone", not "no access".
  if (error instanceof PrivacyDownloadError) {
    if (error.status === 404) {
      return make(
        "conflict",
        "The export file is gone. It may have expired (it lives 24 hours) or it was downloaded five times. Ask for a new export.",
        { reload: true },
      );
    }
    return error.status === 401
      ? make("unauthenticated", "Your session ended. Sign in again.")
      : make("forbidden", NEUTRAL_FORBIDDEN);
  }
  const unavailable = {
    kind: "unavailable",
    message: "The service is not available now. Try again later.",
    retryable: true,
  } as const;
  // Any other answer: nothing was changed, and the same action may be tried again.
  const unknown = {
    kind: "unknown",
    message: "Something went wrong. Nothing was changed. Try again.",
    retryable: true,
  } as const;
  const { kind, message, retryable, reload } = describeStaffFailure<PrivacyFailure["kind"]>(error, {
    conflict:
      "This request changed while you were looking, or you may not decide a request that you made yourself. Nothing was changed. The page shows the latest.",
    forbidden: NEUTRAL_FORBIDDEN,
    on: {
      network: { message: "The network failed. Check the connection and try again." },
      response: {
        kind: "unknown",
        message: "The answer was not in the expected form. Nothing was shown.",
      },
      unauthenticated: "Your session ended. Sign in again.",
      // A refusal for access and a missing id read the same, and the page reads again.
      forbidden: { kind: "forbidden", message: NEUTRAL_FORBIDDEN, reload: true },
      not_found: { kind: "forbidden", message: NEUTRAL_FORBIDDEN, reload: true },
      bad_request: { kind: "bad_request", message: "Check the fields and try again." },
      invalid: { kind: "bad_request", message: "Check the fields and try again." },
      // A throttle is the limit of exports for the day: it is not retried.
      rate_limited: {
        message: "This customer reached the limit of exports for today. Try again tomorrow.",
        retryable: false,
      },
      server: unavailable,
      unavailable,
      other_status: (failure) =>
        failure.status !== null && failure.status >= 500 ? unavailable : unknown,
      timeout: unknown,
      too_large: unknown,
      upload: unknown,
      other: unknown,
    },
  });
  return { kind, message, retryable, reload };
}
